const ACCENTS = ["#ff8bd6", "#a78bfa", "#35d6c0", "#ffd166"];

const projects = [
  {
    id: "agentmagnet",
    role: "Founder",
    title: "Magnet Labs",
    line: "Building AI infrastructure for usage analytics, model compression, and cost optimization, helping companies reduce LLM and GPU inference costs without compromising model quality.",
    programs: "Anthropic Claude Startups · İTÜ Çekirdek · Neo4j Startup Program",
    stats: [
      { number: "−31%", label: "GPU COST" },
      { number: "97%", label: "QUALITY RETAINED" },
    ],
    caption: "Benchmarked on NVIDIA H100 across enterprise tasks in English and Turkish.",
    visual: { type: "beforeAfter", reverse: true },
    image: "/photos/agentmagnet1.png",
    link: "https://agentmagnet.app",
  },
  {
    id: "eti-enterprise",
    role: "Software Developer Intern",
    title: "ETİ Enterprise Systems",
    lines: [
      "Built a security management platform for internal monitoring and cybersecurity processes.",
      "Contributed to web and mobile modules for enterprise operations.",
    ],
    stats: [
      { number: "2", label: "ENTERPRISE SYSTEMS" },
      { number: "WEB + MOBILE", label: "PLATFORMS" },
    ],
    tech: ["React", "React Native", "Node.js", "PostgreSQL", "Azure"],
    visual: { type: "beforeAfter", reverse: true },
    logo: "/photos/eti-logo.png",
  },
  {
    id: "mmpi",
    role: "Founder",
    title: "MMPI Calculator",
    line: "Built and launched a subscription-based psychological assessment platform with automated MMPI scoring, PDF reporting, and AI-assisted interpretation, serving users internationally.",
    stats: [
      { number: "10,000+", label: "USERS" },
      { number: "20+", label: "COUNTRIES" },
    ],
    caption: "Live SaaS product with subscription payments enabled.",
    visual: { type: "bars" },
    image: "/photos/mmpi1.png",
    link: "https://mmpihesaplama.com",
  },
  {
    id: "ismarlio-mobile",
    role: "Software Engineer (Contribution)",
    title: "Ismarlıyo",
    line: "Shipped features across a production-scale food ordering app serving a large active user base.",
    stats: [{ number: "2", label: "PLATFORMS SHIPPED" }],
    caption: "Published on the App Store and Google Play.",
    visual: { type: "beforeAfter" },
    image: "/photos/ısmarlıyo.png",
    portrait: "852 / 1846",
  },
  {
    id: "uniclubs",
    role: "Founder",
    title: "UniClubs",
    line: "Founded and launched a cross-platform mobile platform connecting university students with clubs, communities, and events, taking the product from development to release on iOS and Android.",
    stats: [
      { number: "100+", label: "CLUBS ONBOARDED" },
      { number: "500+", label: "DOWNLOADS" },
    ],
    caption: "Published on the App Store and Google Play.",
    visual: { type: "bars" },
    image: "/photos/uniclubs1.png",
    portrait: "1320 / 2868",
  },
  {
    id: "1e1-ai",
    role: "AI Engineer",
    title: "1e1 AI Tutor Platform",
    line: "Developed LLM-powered tutoring features and backend services for an AI education platform, integrating AI capabilities into a production-oriented learning experience.",
    tech: ["React", "Node.js", "Python", "MongoDB", "Docker"],
    image: "/photos/1e1.png",
    link: "https://www.1e1.ai",
  },
];

const GrowthBars = ({ accent }) => {
  const heights = [10, 16, 22, 29, 36];
  return (
    <svg viewBox="0 0 70 44" width="70" height="44" fill="none">
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * 15}
          y={44 - h}
          width="10"
          height={h}
          rx="2"
          fill={accent}
          opacity={0.35 + (i / (heights.length - 1)) * 0.65}
        />
      ))}
    </svg>
  );
};

const BeforeAfter = ({ accent, reverse }) => {
  const before = reverse ? 34 : 14;
  const after = reverse ? 16 : 34;
  return (
    <svg viewBox="0 0 40 44" width="40" height="44" fill="none">
      <rect x="0" y={44 - before} width="14" height={before} rx="2" fill="#737373" opacity="0.5" />
      <rect x="26" y={44 - after} width="14" height={after} rx="2" fill={accent} />
    </svg>
  );
};

const Visual = ({ visual, accent }) => {
  if (!visual) return null;
  if (visual.type === "bars") return <GrowthBars accent={accent} />;
  return <BeforeAfter accent={accent} reverse={visual.reverse} />;
};

const Card = ({ project: p, index: i }) => {
  const accent = ACCENTS[i % ACCENTS.length];
  const Title = p.link ? "a" : "div";
  const titleProps = p.link
    ? { href: p.link, target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <div className="rounded-[28px] bg-white/[0.03] px-7 py-8 md:px-8 md:py-9">
      {p.logo ? (
        <div className="mb-7 flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-2xl bg-white/[0.02]">
          <img
            src={p.logo}
            alt=""
            draggable={false}
            className="h-28 w-28 object-contain md:h-36 md:w-36"
          />
        </div>
      ) : (
        (p.image || p.video) && (
          <div className="mb-7 overflow-hidden rounded-2xl">
            {p.video ? (
              <video
                src={p.video}
                className="aspect-[16/10] w-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={p.image}
                alt=""
                draggable={false}
                className={`w-full ${
                  p.portrait ? "object-contain" : "aspect-[16/10] object-cover"
                }`}
                style={p.portrait ? { aspectRatio: p.portrait } : undefined}
              />
            )}
          </div>
        )
      )}

      {p.role && (
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500">
          {p.role}
        </div>
      )}

      <Title
        {...titleProps}
        className="mt-1.5 inline-block text-xl font-semibold tracking-tight text-neutral-200 no-underline hover:text-white"
      >
        {p.title}
      </Title>
      {p.lines ? (
        <div className="mt-2.5 space-y-1.5">
          {p.lines.map((l, idx) => (
            <p key={idx} className="text-[14px] leading-relaxed text-neutral-400">
              {l}
            </p>
          ))}
        </div>
      ) : (
        <p className="mt-2.5 text-[14px] leading-relaxed text-neutral-400">
          {p.line}
        </p>
      )}

      {p.programs && (
        <div className="mt-3 text-[11.5px] tracking-wide text-neutral-500">
          {p.programs}
        </div>
      )}

      {p.stats && (
        <div className="mt-7 flex items-center gap-6">
          <Visual visual={p.visual} accent={accent} />
          <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
            {p.stats.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-bold tracking-tight text-white">
                  {s.number}
                </div>
                <div className="mt-0.5 text-[10.5px] uppercase tracking-[0.18em] text-neutral-500">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {p.caption && (
        <div className="mt-3 text-[12px] text-neutral-500">{p.caption}</div>
      )}

      {p.tech && (
        <div className={p.stats ? "mt-5" : "mt-7"}>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-neutral-500">
            Technologies
          </div>
          <div className="mt-1.5 text-[14px] text-neutral-400">
            {p.tech.join(" · ")}
          </div>
        </div>
      )}
    </div>
  );
};

const Projects = () => {
  return (
    <section id="projects" className="w-full bg-[#0d0d0d] py-16">
      <div className="px-6 md:px-10">
        <h2 className="text-5xl md:text-6xl font-semibold tracking-tight text-white/90">
          Featured Projects
        </h2>
      </div>

      <div className="mt-10 columns-1 gap-5 px-6 sm:columns-2 md:px-10 lg:columns-3">
        {projects.map((p, i) => (
          <div key={p.id} className="mb-5 break-inside-avoid">
            <Card project={p} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default Projects;
